import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Info, Loader2, Save, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  useDeleteVoucherProduct,
  useSaveVoucherProduct,
} from './vouchers.api';
import {
  VALIDITY_UNIT,
  type SaveVoucherProduct,
  type ValidityUnit,
  type VoucherProduct,
} from './types';
import { RestrictionPickers } from './restriction-pickers';
import {
  NO_RESTRICTIONS,
  toRestrictionDraft,
  toRestrictionsInput,
  type RestrictionDraft,
} from './voucher-format';

/**
 * Create or edit a voucher in the catalogue.
 *
 * Editing changes what is sold from now on. Every voucher already sold carries
 * its own copy of the terms, so nothing here reaches back into them — the sheet
 * says so, because "I lowered the uses, why do old vouchers still have five?"
 * is the obvious question otherwise.
 *
 * Restrictions are four independent multi-selects. Empty means "any", which is
 * also the component's own convention, and a voucher with all four empty is
 * good for every event.
 */

interface Draft {
  nameEn: string;
  nameEs: string;
  descriptionEn: string;
  descriptionEs: string;
  price: number;
  uses: number;
  validityAmount: number;
  validityUnit: ValidityUnit;
  sortOrder: number;
  isActive: boolean;
  restrictions: RestrictionDraft;
}

/** The owner's default: a single booking, valid for three months. */
const EMPTY: Draft = {
  nameEn: '',
  nameEs: '',
  descriptionEn: '',
  descriptionEs: '',
  price: 0,
  uses: 1,
  validityAmount: 3,
  validityUnit: VALIDITY_UNIT.months,
  sortOrder: 0,
  isActive: true,
  restrictions: NO_RESTRICTIONS,
};

function fromProduct(product: VoucherProduct): Draft {
  return {
    nameEn: product.nameEn ?? '',
    nameEs: product.nameEs ?? '',
    descriptionEn: product.descriptionEn ?? '',
    descriptionEs: product.descriptionEs ?? '',
    price: product.price,
    uses: product.uses,
    validityAmount: product.validityAmount,
    validityUnit: product.validityUnit,
    sortOrder: product.sortOrder,
    isActive: product.isActive,
    restrictions: toRestrictionDraft(product.restrictions),
  };
}

function Field({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-xs font-semibold">{label}</Label>
      {children}
      {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
    </div>
  );
}

export function EditVoucherProductSheet({
  product,
  open,
  onOpenChange,
}: {
  /** `null` opens the sheet on a blank product. */
  product: VoucherProduct | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY);

  const save = useSaveVoucherProduct();
  const remove = useDeleteVoucherProduct();

  useEffect(() => {
    if (!open) return;
    setDraft(product ? fromProduct(product) : EMPTY);
  }, [product, open]);

  const set = (patch: Partial<Draft>) =>
    setDraft((current) => ({ ...current, ...patch }));

  const isEditing = Boolean(product?.id);

  const problem = !draft.nameEn.trim() || !draft.nameEs.trim()
    ? t('vouchers.errors.nameRequired')
    : draft.uses < 1
      ? t('vouchers.errors.usesInvalid')
      : draft.validityAmount < 1
        ? t('vouchers.errors.validityInvalid')
        : draft.price < 0
          ? t('vouchers.errors.priceInvalid')
          : null;

  function submit() {
    if (problem) {
      toast.error(problem);
      return;
    }
    const body: SaveVoucherProduct = {
      nameEn: draft.nameEn.trim(),
      nameEs: draft.nameEs.trim(),
      descriptionEn: draft.descriptionEn.trim() || null,
      descriptionEs: draft.descriptionEs.trim() || null,
      price: draft.price,
      uses: draft.uses,
      validityAmount: draft.validityAmount,
      validityUnit: draft.validityUnit,
      sortOrder: draft.sortOrder,
      isActive: draft.isActive,
      restrictions: toRestrictionsInput(draft.restrictions),
    };

    save.mutate(
      { id: product?.id, body },
      {
        onSuccess: () => {
          toast.success(t('vouchers.saved'));
          onOpenChange(false);
        },
        onError: (error) =>
          toast.error(
            error instanceof Error ? error.message : t('vouchers.saveFailed')
          ),
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-lg"
      >
        <SheetHeader className="border-b">
          <SheetTitle>
            {isEditing ? t('vouchers.editProduct') : t('vouchers.createProduct')}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-5 p-4">
          {isEditing && (
            <div className="bg-muted/50 text-muted-foreground flex gap-2 rounded-lg border p-3 text-xs">
              <Info className="mt-0.5 size-4 shrink-0" />
              <p>{t('vouchers.editNote')}</p>
            </div>
          )}

          <div className="flex items-center justify-between gap-4 py-1">
            <div>
              <Label className="text-xs font-semibold">
                {t('vouchers.onSale')}
              </Label>
              <p className="text-muted-foreground text-xs">
                {t('vouchers.onSaleHint')}
              </p>
            </div>
            <Switch
              checked={draft.isActive}
              onCheckedChange={(isActive) => set({ isActive })}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={`${t('vouchers.name')} (EN)`}>
              <Input
                value={draft.nameEn}
                maxLength={200}
                onChange={(e) => set({ nameEn: e.target.value })}
              />
            </Field>
            <Field label={`${t('vouchers.name')} (ES)`}>
              <Input
                value={draft.nameEs}
                maxLength={200}
                onChange={(e) => set({ nameEs: e.target.value })}
              />
            </Field>
          </div>

          <Field label={`${t('vouchers.description')} (EN)`}>
            <Textarea
              rows={3}
              maxLength={1000}
              value={draft.descriptionEn}
              onChange={(e) => set({ descriptionEn: e.target.value })}
            />
          </Field>
          <Field label={`${t('vouchers.description')} (ES)`}>
            <Textarea
              rows={3}
              maxLength={1000}
              value={draft.descriptionEs}
              onChange={(e) => set({ descriptionEs: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('vouchers.price')} hint={t('vouchers.priceHint')}>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.price}
                onChange={(e) => set({ price: Number(e.target.value) })}
              />
            </Field>
            <Field label={t('vouchers.uses')} hint={t('vouchers.usesHint')}>
              <Input
                type="number"
                min={1}
                step={1}
                value={draft.uses}
                onChange={(e) => set({ uses: Number(e.target.value) })}
              />
            </Field>
          </div>

          <Field label={t('vouchers.validity')} hint={t('vouchers.validityHint')}>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                min={1}
                step={1}
                value={draft.validityAmount}
                onChange={(e) =>
                  set({ validityAmount: Number(e.target.value) })
                }
              />
              <Select
                value={String(draft.validityUnit)}
                onValueChange={(v) =>
                  set({ validityUnit: Number(v) as ValidityUnit })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(VALIDITY_UNIT.days)}>
                    {t('vouchers.days')}
                  </SelectItem>
                  <SelectItem value={String(VALIDITY_UNIT.months)}>
                    {t('vouchers.months')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </Field>

          <Field label={t('vouchers.sortOrder')}>
            <Input
              type="number"
              step={1}
              value={draft.sortOrder}
              onChange={(e) => set({ sortOrder: Number(e.target.value) })}
            />
          </Field>

          <RestrictionPickers
            value={draft.restrictions}
            onChange={(restrictions) => set({ restrictions })}
            enabled={open}
          />

          <Button className="w-full" disabled={save.isPending} onClick={submit}>
            {save.isPending ? <Loader2 className="animate-spin" /> : <Save />}
            {isEditing ? t('vouchers.update') : t('vouchers.create')}
          </Button>

          {isEditing && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  className="text-destructive w-full"
                  disabled={remove.isPending}
                >
                  {remove.isPending ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <Trash2 />
                  )}
                  {t('vouchers.deleteProduct')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('vouchers.deleteConfirmTitle')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('vouchers.deleteConfirmBody')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(product!.id, {
                        onSuccess: () => {
                          toast.success(t('vouchers.deleted'));
                          onOpenChange(false);
                        },
                        onError: (error) =>
                          toast.error(
                            error instanceof Error
                              ? error.message
                              : t('vouchers.deleteFailed')
                          ),
                      })
                    }
                  >
                    {t('vouchers.deleteProduct')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
